import React, { useContext } from 'react';
import { Typography, Box, TableContainer, Table, TableBody, TableCell, TableHead, TableRow, Button, useMediaQuery, useTheme, CircularProgress } from "@mui/material";
import { ThemeContext } from "styled-components";
import FormatDate from "../../../utils/formatters/FormatDate";
import FormatNumber from "../../../utils/formatters/FormatNumber";
import AddStatementRow from "../components/AddStatementRow";

const StatementItem = ({ statement, theme, isOwner, formatLevel }) => {
    return (
        <TableRow>
            {/* Left spacer column - 56px spacing - matches table header */}
            <TableCell sx={{ width: '56px', minWidth: '56px', maxWidth: '56px', p: 0 }}></TableCell>
            <TableCell sx={{ color: theme.colors.text, pl: 0, fontSize: '1.1rem' }}>
                {FormatDate(statement.created_at, formatLevel)}
            </TableCell>
            <TableCell align="right" sx={{ color: theme.colors.text, fontWeight: 500, padding: '16px 0', fontSize: '1.1rem' }}>
                ${statement.cost ? FormatNumber(statement.cost) : '0'}
            </TableCell>
            {/* Right spacer column - 56px spacing - matches table header */}
            <TableCell sx={{ width: '56px', minWidth: '56px', maxWidth: '56px', p: 0 }}></TableCell>
        </TableRow>
    );
};

const FormStatements = ({ onSubmit, statements, templateId, isOwner, showMoreButton, onShowMore, loadingMore, hasMore, accumulatedLength, hydrating = false, expandedView = false, headerAction, ...props }) => {
    const theme = useContext(ThemeContext);
    const mediaTheme = useTheme();
    // Use compact format (0) on extra small screens, full format (1) on sm and up
    const isExtraSmall = useMediaQuery(mediaTheme.breakpoints.down('sm'));
    const formatLevel = isExtraSmall ? 0 : 1;

    const tableContainerSx = {
        mb: 0,
        pb: 0,
        mt: 0,
        pt: 0,
        mr: 0,
        ml: 0,
        border: 'none !important',
        borderBottom: 'none !important',
        borderTop: 'none !important',
        outline: 'none !important',
        overflow: 'hidden',
        ...(expandedView
            ? {
                borderRadius: 0,
                backgroundColor: 'transparent',
                boxShadow: 'none',
            }
            : {
                borderRadius: 2,
                backgroundColor: theme.colors.tableBackground || 'white',
                boxShadow: theme.shadows?.table || '0 4px 12px rgba(0, 0, 0, 0.15)',
            }),
        '&::before': { display: 'none !important' },
        '&::after': { display: 'none !important' },
        '& .MuiTable-root': {
            border: 'none !important',
            borderBottom: 'none !important',
            borderTop: 'none !important',
            marginBottom: '0 !important',
            '& .MuiTableBody-root': {
                border: 'none !important',
                borderBottom: 'none !important',
                '& .MuiTableRow-root:last-child .MuiTableCell-root': {
                    borderBottom: 'none !important',
                },
            },
        },
    };

    return (
        <>
            <Box
                sx={{
                    width: '100%',
                    border: 'none',
                    borderBottom: 'none',
                    mb: expandedView ? 0 : '40px',
                    '& .MuiTableContainer-root': {
                        borderBottom: 'none !important',
                        '&::after': { display: 'none !important' },
                    },
                    ...props,
                }}
            >
                {/* Statements List - Table Format - Always show table, even when empty */}
                <TableContainer
                    component={Box}
                    elevation={0}
                    sx={tableContainerSx}
                >
                    <Table sx={{ 
                        border: 'none !important', 
                        borderBottom: 'none !important',
                        borderCollapse: 'separate',
                        borderSpacing: 0,
                    }}>
                        <TableHead>
                            <TableRow>
                                {/* Left spacer column - 56px spacing / header action */}
                                <TableCell sx={{ width: '56px', minWidth: '56px', maxWidth: '56px', p: 0, verticalAlign: 'middle' }}>
                                    {headerAction ? (
                                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', height: '100%' }}>
                                            {headerAction}
                                        </Box>
                                    ) : null}
                                </TableCell>
                                <TableCell sx={{ color: theme.colors.brandSecondary, fontWeight: 700, pl: 0, fontSize: '1.75rem', py: 2 }}>Statements</TableCell>
                                <TableCell align="right" sx={{ color: theme.colors.text, fontWeight: 600, padding: '16px 0' }}></TableCell>
                                {/* Right spacer column - 56px spacing */}
                                <TableCell sx={{ width: '56px', minWidth: '56px', maxWidth: '56px', p: 0 }}></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {/* While dehydrated: one empty row with loading animation, no add-statement row */}
                            {hydrating ? (
                                <TableRow>
                                    <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                                        <CircularProgress sx={{ color: theme.colors?.primary || theme.colors?.brandSecondary }} />
                                    </TableCell>
                                </TableRow>
                            ) : (
                                <>
                            {/* Statements - sorted by date (newest first) */}
                            {statements && Array.isArray(statements) && statements.length > 0 ? (
                                statements.sort((a, b) => {
                                    return new Date(b.created_at) - new Date(a.created_at);
                                }).map((statement) => (
                                    <StatementItem
                                        key={statement.id}
                                        statement={statement}
                                        theme={theme}
                                        isOwner={isOwner}
                                        formatLevel={formatLevel}
                                    />
                                ))
                            ) : null}
                            {showMoreButton && (
                                <TableRow>
                                    <TableCell colSpan={4} sx={{ py: 1.5, borderBottom: 'none', verticalAlign: 'middle' }}>
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            disabled={loadingMore || !hasMore}
                                            onClick={onShowMore}
                                            sx={{
                                                color: theme?.colors?.brandSecondary,
                                                borderColor: theme?.colors?.brandSecondary,
                                                '&:hover': {
                                                    borderColor: theme?.colors?.brandSecondary,
                                                    backgroundColor: theme?.colors?.brandSecondary ? `${theme.colors.brandSecondary}14` : undefined,
                                                },
                                            }}
                                        >
                                            {loadingMore ? 'Loading...' : 'Show more'}
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            )}
                            {/* Add Statement Row - hidden while hydrating */}
                            <AddStatementRow
                                key="add-statement-row"
                                templateId={templateId}
                                onSubmit={onSubmit}
                                isOwner={isOwner}
                                formatLevel={formatLevel}
                            />
                                </>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>
        </>
    );
};

export default FormStatements;
